import React, { useState, useEffect, useRef } from 'react';

export const StatsSection = ({
  statNumber = "1,000,000",
  statSuffix = "+",
  statLabel = "shared experiences",
  subtext = "Stories and insights contributed by real patients",
  leadIn = "Shared by patients across more than 2,500 health conditions — including:",
  backgroundColor = "#F9F7F4"
}) => {
  const sectionRef = useRef(null);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const targetCount = 1000000;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Animate count when visible
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2500;
    const steps = 60;
    const increment = targetCount / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetCount) {
        current = targetCount;
        clearInterval(timer);
      }
      if (countRef.current) {
        countRef.current.textContent = Math.floor(current).toLocaleString() + statSuffix;
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, statSuffix]);

  return (
    <section
      ref={sectionRef}
      style={{
        backgroundColor: backgroundColor,
        padding: '80px 24px 60px',
        textAlign: 'center',
        position: 'relative'
      }}
    >
      {/* Big stat number */}
      <div
        style={{
          fontFamily: '"Source Serif Pro", Georgia, serif',
          fontSize: 'clamp(64px, 12vw, 120px)',
          fontWeight: '700',
          color: '#111111',
          lineHeight: '1',
          marginBottom: '8px'
        }}
      >
        <span className="notranslate" style={{ position: 'relative', display: 'inline-block' }}>
          {/* Highlight effect */}
          <span
            style={{
              position: 'absolute',
              left: '-2%',
              right: '-2%',
              bottom: '5%',
              height: '35%',
              background: 'linear-gradient(90deg, rgba(250, 190, 192, 0.6) 0%, rgba(247, 192, 138, 0.5) 50%, rgba(251, 244, 188, 0.4) 100%)',
              transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
              transformOrigin: 'left',
              transition: 'transform 1.2s ease-out 0.3s',
              zIndex: 0,
              borderRadius: '4px'
            }}
          />
          <span ref={countRef} style={{ position: 'relative', zIndex: 1 }}>
            0{statSuffix}
          </span>
        </span>
      </div>

      {/* Stat label */}
      <div
        style={{
          fontFamily: '"Source Serif Pro", Georgia, serif',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '400',
          color: '#111111',
          marginBottom: '16px'
        }}
      >
        {statLabel}
      </div>

      {/* Subtext */}
      <p
        style={{
          fontSize: '18px',
          color: '#4A4A4A',
          maxWidth: '500px',
          margin: '0 auto',
          lineHeight: '1.6'
        }}
      >
        {subtext}
      </p>
    </section>
  );
};
